<template>

  <el-main id="register-alternate">

    <div v-if="success" id="nomination-success">
      <h3>Registration successful</h3>
      <p>Thank you for registering an alternate.</p>
      <p>We have now emailed this alternate notifying them of this.</p>
      <p>Once the alternate either accepts or declines this registration you will be notified via email.</p>
    </div>


    <!-- Fail to initialise -->
    <div v-else-if="initError">
      <p>It seems that you have already nominated an alternate, or your position as a State Council Delegate is no
        longer valid.</p>
      <p>Please call head office on <a href="tel:0263560300" class="tel">02 6356 0300</a> for further details.</p>
    </div>


    <!-- Error in submission -->
    <div v-else-if="nominationError" id="nomination-error">
      <h3>Sorry, we encountered an error</h3>
      <p>{{ nominationError }}</p>
      <p>Please call head office on <a href="tel:0263560300" class="tel">02 6356 0300</a> for further details.</p>
      <el-button
          type="primary"
          @click="retry"
      >
        Try again
      </el-button>
    </div>


    <!-- Submission form -->
    <div v-else v-loading="loading">

      <div v-html="nominationProcessDescription" id="nomination-process-description"/>

      <el-card
          id="delegate-details"
          class="person-details"
          shadow="never"
      >
        <h4>Your details</h4>
        <h3>{{ delegate.fullName }}</h3>
        <p v-if="delegate.mobile">{{ delegate.mobile }}</p>
        <p>{{ delegate.email }}</p>
      </el-card>

      <el-form
          ref="nominationForm"
          :model="form"
          :rules="rules"
          label-position="top"
          class="body-normal"
      >

        <el-alert
            v-if="validationError"
            :title="validationError"
            type="warning"
            effect="dark"
        />

        <el-form-item
            label="Select the Body you wish to nominate an alternate for"
            prop="branch"
        >
          <el-select v-model="form.branch" placeholder="Select Body ">
            <el-option
                v-for="branch in branchOptions"
                :key="branch.value"
                :label="branch.label"
                :value="branch.value"
            />
          </el-select>
        </el-form-item>

        <p id="rules">
          <a href="#" @click.prevent="showModal = !showModal">Rules for who is eligible to be nominated as an alternate
            can be found by clicking here.</a>
        </p>

        <h3>Proposed alternate</h3>
        <el-row :gutter="20">
          <el-col :sm="12">
            <el-form-item label="First Name" prop="nominee.firstName">
              <el-input
                  v-model="form.nominee.firstName"
                  :disabled="!branchOptions.length"
                  placeholder="First name"
                  required
              />
            </el-form-item>
          </el-col>
          <el-col :sm="12">
            <el-form-item label="Last Name" prop="nominee.lastName">
              <el-input
                  v-model="form.nominee.lastName"
                  :disabled="!branchOptions.length"
                  placeholder="Last name"
                  required
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :sm="12">
            <el-form-item label="Email" prop="nominee.email">
              <el-input
                  v-model="form.nominee.email"
                  :disabled="!branchOptions.length"
                  placeholder="Email"
                  required
              />
            </el-form-item>
          </el-col>
          <el-col :sm="12">
            <el-form-item label="Mobile" prop="nominee.mobile">
              <el-input
                  v-model="form.nominee.mobile"
                  :disabled="!branchOptions.length"
                  placeholder="Mobile"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <p class="submit">
          <el-button
              type="primary"
              :disabled="!branchOptions.length"
              @click="submitNomination"
          >
            Submit
          </el-button>
        </p>
      </el-form>
    </div>


    <ModalShell
        id="nomination-modal"
        title="Alternate Eligibility Rules"
        :show="showModal"
        @close="showModal = false"
    >
      <div v-html="nomineeRules"/>
    </ModalShell>

  </el-main>

</template>

<script>

import {$axios} from '../../_helper/axiosWrapper.js'
import {Person} from './person.js'
import {mapState} from 'vuex'

import ModalShell from '../utilities/ModalShell.vue'

export default {
  name: "NominationForm",
  props: {
    credentials: {}
  },
  components: {
    ModalShell
  },
  data() {
    return {
      token: null,
      delegate: new Person(),
      branchOptions: [],
      form: {
        branch: '',
        nominee: new Person()
      },
      loading: false,
      nominationProcessDescription: '',
      nomineeRules: '',
      initError: null,
      nominationError: null,
      validationError: null,
      success: false,
      rules: {
        branch: [
          {required: true, message: 'Please select a branch', trigger: 'submit'},
        ],
        nominee: {
          firstName: [
            {
              required: true,
              message: 'Please enter a first name for your alternate',
              whitespace: true,
              trigger: 'blur'
            },
            {min: 2, message: 'Should be at least 2 characters', trigger: 'blur'}
          ],
          lastName: [
            {required: true, message: 'Please enter a last name for your alternate', whitespace: true, trigger: 'blur'},
            {min: 2, message: 'Should be at least 2 characters', trigger: 'blur'}
          ],
          email: [
            {type: 'email', message: 'Please enter a valid email for your alternate', trigger: 'blur'},
            {required: true, message: 'Please enter an email for your alternate', trigger: 'blur'}
          ],
          mobile: [
            {
              pattern: /^04\d{2} ?\d{3} ?\d{3}$/,
              message: 'Please enter a valid mobile number for your alternate in the form 04XX XXX XXX',
              trigger: 'blur'
            }
          ]
        }
      },
      showModal: false
    }
  },
  computed: {
    ...mapState(['baseURL'])
  },
  watch: {
    credentials() {
      this.init()
    }
  },
  methods: {
    async init() {

      try {

        this.loading = true

        const queryParams = Object.entries(this.credentials).map(([key, value]) => `${key}=${value}`).join('&')

        // query nominations endpoint
        const initiateResponse = await $axios.post({
          url: `/nominations/initiate?${queryParams}`,
          baseURL: this.baseURL,
          // data: this.credentials
        }, 'Error getting initiation response')


        // handle errors
        // todo: are these errors even in play?
        if (initiateResponse?.status !== 200) {

          const message = initiateResponse.data?.message || 'Sorry, there was an error processing your request. Please try again later.'

          switch (initiateResponse.status) {
            case 401:
              this.nominationError = 'Sorry, we could not find a member with those details. Please check the link and try again.'
              break
            case 403:
              this.nominationError = 'Sorry, you are not authorised to nominate an alternate.'
              break
            case 404:
              this.nominationError = 'Sorry, we could not find details for your nomination. Please check the link and try again.'
              break
            case 409:
              this.nominationError = 'You have already nominated an alternate. Please contact us if you need to change your alternate.'
              break
            default:
              this.nominationError = message
          }

          // bug out
          this.initError = true
          return
        }


        // Populate form
        const {
          memberDetails: {fullName, mobile, email, id},
          token,
          branches,
          nominationProcessDescription,
          nomineeRules
        } = initiateResponse.data

        // populate details
        // todo: sanitize?
        this.nominationProcessDescription = nominationProcessDescription
        this.nomineeRules = nomineeRules

        this.branchOptions = branches.map(branch => ({
          label: branch,
          value: branch
        }))
        if (this.branchOptions.length === 1) {
          this.form.branch = this.branchOptions[0].value
        }

        this.delegate = {id, fullName, email, mobile}

        this.token = token

      } finally {
        this.loading = false
      }

    },
    async submitNomination() {
      this.$refs.nominationForm.validate(async valid => {
        if (valid) {

          try {
            this.loading = true

            const payload = {
              branch: this.form.branch,
              firstName: this.form.nominee.firstName,
              lastName: this.form.nominee.lastName,
              email: this.form.nominee.email,
              eventCode: this.credentials.eventCode,
              memberID: this.delegate.id,
              token: this.token,
            }
            // only include mobile if available
            if (this.form.nominee.mobile) {
              payload.mobile = this.form.nominee.mobile
            }

            const nominationResponse = await $axios.post({
              url: '/nominations/alternate',
              baseURL: this.baseURL,
              headers: {Authorization: `Bearer ${this.token}`},
              data: payload
            }, 'Sorry, we had a problem confirming your nomination.')


            // handle errors
            if (nominationResponse.status !== 202) {

              // if there's a message in the payload use it
              const responseMessage = nominationResponse.data?.message || null

              // other response
              this.nominationError = responseMessage ?
                  `We encountered the following problem: ${responseMessage}`
                  : 'Sorry, we cannot process your nomination at this time. Please try again later.'

              return
            }

            // handle 202 responses
            const {id: nomineeId} = nominationResponse.data

            if (nomineeId) {
              this.success = true
            } else {
              const reason = nominationResponse.data?.message || 'Unknown reason'
              this.nominationError = `Sorry, we cannot process your nomination. Reason: (${reason})`
            }

          } finally {
            this.loading = false
          }

        } else {
          this.validationError = 'There appears to be an error in your nominee information. Please check the details and try again.'
          return false
        }
      })
    },
    retry() {
      // window.location.reload()
      this.nominationError = null
    }
  }

}
</script>

<style lang="scss">
.body-normal .el-form-item__label {
  font: inherit;
}

</style>